import React, { useContext, useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../../../firebase";
import { AppContext } from "../../../../store/AppContext";
import { CustomButton } from "../../../../components/CustomButton/CustomButton";
import CustomDialog from "../../../../components/CustomDialog/CustomDialog";
import { Switch, CircularProgress, Box, useMediaQuery, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import "../../coupons.css";

const fmtDate = (ts) => {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const scopeLabel = (c) => {
  const p = (c.applicableProducts || []).length;
  const cat = (c.applicableCategories || []).length;
  if (p && cat) return `${p} product(s), ${cat} cat.`;
  if (p) return `${p} product(s)`;
  if (cat) return `${cat} categories`;
  return "All items";
};

const discountLabel = (c) =>
  c.discountType === "percentage"
    ? `${c.discountValue}% off`
    : `₹${c.discountValue} off`;

const CouponCard = ({ coupon, onEdit, onDelete, onToggle }) => (
  <div className="coupon-card">
    <div className="coupon-card-header">
      <span className="coupon-card-code">{(coupon.code || "").toUpperCase()}</span>
      <Switch
        size="small"
        checked={coupon.active !== false}
        onChange={() => onToggle(coupon)}
      />
    </div>
    <div className="coupon-card-grid">
      <div>
        <div className="coupon-card-label">Discount</div>
        <div className="coupon-card-value">{discountLabel(coupon)}</div>
      </div>
      <div>
        <div className="coupon-card-label">Scope</div>
        <div className="coupon-card-value">{scopeLabel(coupon)}</div>
      </div>
      <div>
        <div className="coupon-card-label">Min order</div>
        <div className="coupon-card-value">
          {coupon.minOrderValue ? `₹${coupon.minOrderValue}` : "—"}
        </div>
      </div>
      <div>
        <div className="coupon-card-label">Valid till</div>
        <div className="coupon-card-value">{fmtDate(coupon.endDate)}</div>
      </div>
      <div>
        <div className="coupon-card-label">Uses</div>
        <div className="coupon-card-value">
          {coupon.usageCount || 0}
          {coupon.usageLimit ? ` / ${coupon.usageLimit}` : ""}
        </div>
      </div>
    </div>
    <div className="coupon-card-actions">
      <span style={{ fontSize: 13, color: coupon.active !== false ? "#2e7d32" : "#999" }}>
        {coupon.active !== false ? "Active" : "Inactive"}
      </span>
      <div style={{ display: "flex", gap: 12 }}>
        <EditIcon
          sx={{ cursor: "pointer", color: "#007BFF" }}
          onClick={() => onEdit(coupon)}
        />
        <DeleteOutlineOutlinedIcon
          sx={{ cursor: "pointer", color: "#d32f2f" }}
          onClick={() => onDelete(coupon)}
        />
      </div>
    </div>
  </div>
);

const Coupons = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { setCouponTabInfo } = useContext(AppContext);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "coupons"),
        orderBy("createdDate", "desc")
      );
      const snap = await getDocs(q);
      setCoupons(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      try {
        const snap = await getDocs(collection(db, "coupons"));
        setCoupons(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Failed to fetch coupons:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const toggleActive = async (coupon) => {
    try {
      await updateDoc(doc(db, "coupons", coupon.id), {
        active: !coupon.active,
        updatedDate: new Date(),
      });
      setCoupons((prev) =>
        prev.map((c) => (c.id === coupon.id ? { ...c, active: !c.active } : c))
      );
    } catch (e) {
      console.error("Failed to toggle coupon:", e);
      alert("Failed to update coupon");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, "coupons", deleteTarget.id));
      setCoupons((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    } catch (e) {
      console.error("Failed to delete coupon:", e);
      alert("Failed to delete coupon");
    } finally {
      setDeleteTarget(null);
    }
  };

  const openEdit = (coupon) =>
    setCouponTabInfo((prev) => ({ ...prev, selectedCoupon: coupon }));

  return (
    <div className="coupons-page">
      <div className="coupons-panel">
        <div className="coupons-page-header">
          <h2>Coupons</h2>
          <CustomButton
            className="coupons-add-btn"
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ textTransform: "none" }}
            fullWidth={isMobile}
            onClick={() =>
              setCouponTabInfo((prev) => ({ ...prev, addCoupon: true }))
            }
          >
            Add new coupon
          </CustomButton>
        </div>

      {loading ? (
        <Box display="flex" justifyContent="center" padding="40px">
          <CircularProgress />
        </Box>
      ) : coupons.length === 0 ? (
        <div className="coupons-empty">
          <h3>No coupons yet</h3>
          <p>Create your first coupon to offer discounts at checkout.</p>
        </div>
      ) : isMobile ? (
        <div>
          {coupons.map((c) => (
            <CouponCard
              key={c.id}
              coupon={c}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              onToggle={toggleActive}
            />
          ))}
        </div>
      ) : (
        <div className="coupons-table-wrap">
          <div className="coupons-table-inner">
            <table className="coupons-table">
              <colgroup>
                <col style={{ width: "12%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "17%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="col-code">Code</th>
                  <th>Discount</th>
                  <th className="col-scope">Scope</th>
                  <th>Min order</th>
                  <th>Valid till</th>
                  <th>Uses</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id}>
                    <td className="col-code">{(c.code || "").toUpperCase()}</td>
                    <td>{discountLabel(c)}</td>
                    <td className="col-scope">{scopeLabel(c)}</td>
                    <td>{c.minOrderValue ? `₹${c.minOrderValue}` : "—"}</td>
                    <td>{fmtDate(c.endDate)}</td>
                    <td>
                      {c.usageCount || 0}
                      {c.usageLimit ? ` / ${c.usageLimit}` : ""}
                    </td>
                    <td>
                      <div className="col-status">
                        <Switch
                          size="small"
                          checked={c.active !== false}
                          onChange={() => toggleActive(c)}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="col-actions">
                        <EditIcon
                          sx={{ cursor: "pointer", color: "#007BFF" }}
                          onClick={() => openEdit(c)}
                        />
                        <DeleteOutlineOutlinedIcon
                          sx={{ cursor: "pointer", color: "#d32f2f" }}
                          onClick={() => setDeleteTarget(c)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>

      <CustomDialog
        open={!!deleteTarget}
        title="Delete coupon"
        type="error"
        description={`Are you sure you want to delete "${
          (deleteTarget?.code || "").toUpperCase()
        }"? This cannot be undone.`}
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onPrimaryClick={confirmDelete}
        onSecondaryClick={() => setDeleteTarget(null)}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default Coupons;
